import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, Trophy, Medal, Award } from "lucide-react";

import { reportApi } from "@/lib/extensions-api";
import { API_BASE_URL } from "@/lib/constants";
import { formatCurrency, formatNumber } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Select } from "@/components/ui/Select";
import { Button } from "@/components/ui/Button";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { CopyableId } from "@/components/ui/CopyableId";
import type { ReportPeriod } from "@/types/report";

const RANK_ICONS: Record<number, any> = {
  1: { icon: Trophy, color: "bg-yellow-100 text-yellow-700" },
  2: { icon: Medal,  color: "bg-surface-200 text-surface-700" },
  3: { icon: Award,  color: "bg-orange-100 text-orange-700" },
};

export function TopUsersReportPage() {
  const [period, setPeriod] = useState<ReportPeriod>("monthly");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-top-users", period],
    queryFn: () => reportApi.topUsers({ period, limit: 20 }),
  });

  const pdfUrl = `${API_BASE_URL}/admin/reports/export/pdf?type=top_users&period=${period}`;

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">Top Users</h1>
          <p className="text-sm text-surface-500 mt-1">
            Ranked by completed sales in the selected period.
          </p>
        </div>
        <a href={pdfUrl} target="_blank" rel="noreferrer">
          <Button leftIcon={<Download className="h-4 w-4" />}>Download PDF</Button>
        </a>
      </header>

      <Card>
        <CardBody className="flex items-end gap-3">
          <div className="w-48">
            <Select label="Period"
                     value={period}
                     onChange={(e) => setPeriod(e.target.value as ReportPeriod)}
                     options={[
                       { value: "today",   label: "Today" },
                       { value: "weekly",  label: "This Week" },
                       { value: "monthly", label: "This Month" },
                       { value: "yearly",  label: "This Year" },
                     ]} />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader><CardTitle>Rankings</CardTitle></CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : (data?.top_users ?? []).length === 0 ? (
            <EmptyState icon={Trophy} title="No qualifying users in this period" />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Rank</TH>
                  <TH>User</TH>
                  <TH>Name</TH>
                  <TH className="text-right">Transactions</TH>
                  <TH className="text-right">Total Sales</TH>
                </TR>
              </THead>
              <tbody>
                {data!.top_users.map((u) => {
                  const rank = RANK_ICONS[u.rank];
                  return (
                    <TR key={u.user_id}>
                      <TD>
                        {rank ? (
                          <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg ${rank.color}`}>
                            <rank.icon className="h-3.5 w-3.5" />
                            <span className="font-bold">#{u.rank}</span>
                          </div>
                        ) : (
                          <span className="font-mono text-surface-500">#{u.rank}</span>
                        )}
                      </TD>
                      <TD><CopyableId value={u.user_id} /></TD>
                      <TD className="font-medium">{u.full_name}</TD>
                      <TD className="text-right">{formatNumber(u.transaction_count)}</TD>
                      <TD className="text-right font-bold text-brand-700">
                        {formatCurrency(u.total_sales)}
                      </TD>
                    </TR>
                  );
                })}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
